import React, { useState, useRef } from 'react';
import {
  Shield, User, Building2, ArrowRight, ArrowLeft, Check, X, Loader2,
  Upload, Camera, FileText, AlertCircle, Lock,
} from 'lucide-react';

/**
 * AfriLink Verify Widget — fully white-labeled identity verification.
 *
 * The end user only ever sees AfriLink branding. Backend transparently
 * routes to Smile ID / Mono Lookup based on the selected check type.
 *
 * Flow:
 *   1. Choose mode (Individual / Business)
 *   2. Enter ID (BVN, NIN, CAC, TIN)
 *   3. Optional: document + selfie for enhanced (Tier 2/3)
 *   4. Result screen — branded "Verified by AfriLink"
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4000'
    : 'https://api.afrilink.io');

const BRAND = {
  green: '#1D9E75',
  greenDark: '#0F6E56',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
};

// ─── ID type configs ────────────────────────────────────
const ID_TYPES = {
  individual: [
    {
      id: 'BVN',
      label: 'BVN',
      description: 'Bank Verification Number',
      placeholder: '11-digit BVN',
      maxLength: 11,
      pattern: /^\d{11}$/,
      endpoint: '/api/identity/bvn',
      bodyKey: 'bvn',
      tier: 'TIER 1',
    },
    {
      id: 'NIN',
      label: 'NIN',
      description: 'National Identification Number',
      placeholder: '11-digit NIN',
      maxLength: 11,
      pattern: /^\d{11}$/,
      endpoint: '/api/identity/nin',
      bodyKey: 'nin',
      tier: 'TIER 1',
    },
  ],
  business: [
    {
      id: 'CAC',
      label: 'CAC',
      description: 'Business Registration (RC / BN)',
      placeholder: 'e.g. RC1234567',
      maxLength: 20,
      pattern: /^[A-Za-z]{1,3}\d{4,12}$/,
      endpoint: '/api/identity/cac',
      bodyKey: 'rcNumber',
      tier: 'KYB',
    },
    {
      id: 'TIN',
      label: 'TIN',
      description: 'Tax Identification Number',
      placeholder: 'e.g. 12345678-0001',
      maxLength: 20,
      pattern: /^[\d-]+$/,
      endpoint: '/api/identity/tin',
      bodyKey: 'tin',
      tier: 'KYB',
    },
  ],
};

// ─── Sub-components ─────────────────────────────────────
const Logo = ({ size = 40 }) => (
  <div
    className="flex items-center justify-center rounded-xl text-white font-bold flex-shrink-0"
    style={{
      width: size,
      height: size,
      background: BRAND.green,
      fontSize: size * 0.32,
      letterSpacing: '-0.02em',
    }}
  >
    AL
  </div>
);

const ProgressBar = ({ current, total }) => (
  <div className="flex gap-1.5 mb-6">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className="h-1 flex-1 rounded-full transition-all"
        style={{ background: i < current ? BRAND.green : '#E5E7EB' }}
      />
    ))}
  </div>
);

const TrustBadge = () => (
  <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-100">
    <Lock size={12} className="text-gray-400" />
    <span className="text-[11px] text-gray-400 font-medium tracking-wide">
      Bank-grade encryption · AES-256
    </span>
  </div>
);

const HeaderBar = ({ onBack }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
    <div className="flex items-center gap-2.5">
      <Logo size={28} />
      <span className="font-semibold text-gray-900 text-[15px]">AfriLink</span>
    </div>
    {onBack ? (
      <button
        onClick={onBack}
        className="text-gray-400 hover:text-gray-700 p-1 -m-1 transition"
        aria-label="Go back"
      >
        <ArrowLeft size={18} />
      </button>
    ) : (
      <span className="text-[10px] text-gray-400 font-semibold tracking-wider uppercase">
        Verify
      </span>
    )}
  </div>
);

// ─── Main component ─────────────────────────────────────
const VerifyWidget = ({
  apiKey,
  userId = 'user_' + Date.now(),
  onComplete,
  onClose,
  enableEnhanced = true,
}) => {
  const [step, setStep] = useState('intro'); // intro | mode | id | enhanced | submitting | result
  const [mode, setMode] = useState(null); // 'individual' | 'business'
  const [idType, setIdType] = useState(null);
  const [idValue, setIdValue] = useState('');
  const [fullName, setFullName] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const docInputRef = useRef(null);
  const selfieInputRef = useRef(null);

  const reset = () => {
    setStep('intro');
    setMode(null);
    setIdType(null);
    setIdValue('');
    setFullName('');
    setDocFile(null);
    setSelfieFile(null);
    setError('');
    setResult(null);
  };

  const callApi = async (endpoint, body) => {
    const res = await fetch(API_BASE + endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'x-api-key': apiKey } : {}),
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Verification failed. Please try again.');
    }
    return data;
  };

  const submitVerification = async () => {
    if (!idType.pattern.test(idValue.trim())) {
      setError(`Please enter a valid ${idType.label}`);
      return;
    }
    setError('');
    setStep('submitting');
    try {
      const body = { [idType.bodyKey]: idValue.trim() };
      if (fullName.trim()) body.fullName = fullName.trim();
      const res = await callApi(idType.endpoint, body);
      const verified = res.data?.verified === true || res.success === true;
      const status = verified
        ? 'verified'
        : res.data?.status === 'REVIEW'
        ? 'review'
        : 'failed';
      setResult({
        status,
        idType: idType.label,
        tier: idType.tier,
        firstName: res.data?.firstName || res.data?.companyName || '',
        lastName: res.data?.lastName || '',
        middleName: res.data?.middleName || '',
        dateOfBirth: res.data?.dateOfBirth || res.data?.registrationDate || '',
        details: res.data || {},
      });
      setStep('result');
      if (onComplete) onComplete({ verified, idType: idType.label, data: res.data });
    } catch (err) {
      setError(err.message);
      setStep('id');
    }
  };

  const handleFile = (file, setter) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('File must be under 5MB');
      return;
    }
    setter(file);
    setError('');
  };

  const startOver = () => reset();

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <div
      className="bg-white rounded-3xl overflow-hidden border border-gray-200 w-full max-w-md mx-auto"
      style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08)' }}
    >
      <HeaderBar
        onBack={
          step === 'mode' ? () => setStep('intro')
            : step === 'id' ? () => setStep('mode')
            : step === 'enhanced' ? () => setStep('id')
            : null
        }
      />

      {/* INTRO ─────────────────────────────────────────── */}
      {step === 'intro' && (
        <div className="p-8 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ background: BRAND.greenSoft }}
          >
            <Shield size={28} style={{ color: BRAND.green }} />
          </div>
          <h2 className="text-[22px] font-bold text-gray-900 mb-2 tracking-tight">
            Verify your identity
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-7 max-w-xs mx-auto">
            We need to confirm a few details to keep your account secure.
            This usually takes under 60 seconds.
          </p>
          <div className="space-y-2.5 mb-7 text-left">
            {[
              { icon: Lock, text: 'Encrypted end-to-end' },
              { icon: Shield, text: 'Used only for verification' },
              { icon: Check, text: 'Trusted by leading fintechs' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 px-1">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: BRAND.greenSoft }}
                >
                  <Icon size={13} style={{ color: BRAND.green }} />
                </div>
                <span className="text-[13px] text-gray-600">{text}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setStep('mode')}
            className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition hover:opacity-90"
            style={{ background: BRAND.green }}
          >
            Get started
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="w-full mt-2 py-2.5 text-xs text-gray-400 hover:text-gray-600 transition"
            >
              Cancel
            </button>
          )}
          <TrustBadge />
        </div>
      )}

      {/* MODE PICKER ──────────────────────────────────── */}
      {step === 'mode' && (
        <div className="p-6">
          <ProgressBar current={1} total={enableEnhanced ? 4 : 3} />
          <h2 className="text-lg font-bold text-gray-900 mb-1 tracking-tight">
            Who are we verifying?
          </h2>
          <p className="text-[13px] text-gray-500 mb-6">
            Choose the option that fits your situation.
          </p>
          <div className="space-y-2.5">
            {[
              {
                id: 'individual',
                label: 'Individual',
                desc: 'Verify with BVN or NIN',
                icon: User,
              },
              {
                id: 'business',
                label: 'Business',
                desc: 'Verify with CAC or TIN',
                icon: Building2,
              },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = mode === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    setMode(opt.id);
                    setIdType(ID_TYPES[opt.id][0]);
                    setStep('id');
                  }}
                  className="w-full flex items-center gap-3.5 p-4 rounded-2xl border text-left transition hover:border-gray-300"
                  style={{
                    borderColor: active ? BRAND.green : '#E5E7EB',
                    background: active ? BRAND.greenSoft : '#fff',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: active ? BRAND.green : '#F3F4F6',
                      color: active ? '#fff' : '#6B7280',
                    }}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-[14px]">{opt.label}</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">{opt.desc}</p>
                  </div>
                  <ArrowRight size={16} className="text-gray-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
          <TrustBadge />
        </div>
      )}

      {/* ID INPUT ─────────────────────────────────────── */}
      {step === 'id' && idType && (
        <div className="p-6">
          <ProgressBar current={2} total={enableEnhanced ? 4 : 3} />
          <h2 className="text-lg font-bold text-gray-900 mb-1 tracking-tight">
            Enter your {idType.description}
          </h2>
          <p className="text-[13px] text-gray-500 mb-5">
            This stays encrypted. We never share it.
          </p>

          {/* ID type selector — pills */}
          {ID_TYPES[mode].length > 1 && (
            <div className="flex gap-1.5 mb-5 p-1 bg-gray-100 rounded-xl">
              {ID_TYPES[mode].map((t) => {
                const active = t.id === idType.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setIdType(t);
                      setIdValue('');
                      setError('');
                    }}
                    className="flex-1 py-2 rounded-lg text-[12px] font-semibold transition"
                    style={{
                      background: active ? '#fff' : 'transparent',
                      color: active ? '#111' : '#6B7280',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}

          <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 tracking-wide uppercase">
            {idType.label} Number
          </label>
          <input
            type="text"
            inputMode={idType.id === 'CAC' || idType.id === 'TIN' ? 'text' : 'numeric'}
            value={idValue}
            onChange={(e) => {
              setIdValue(e.target.value);
              setError('');
            }}
            maxLength={idType.maxLength}
            placeholder={idType.placeholder}
            className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-gray-400 transition font-mono tracking-wider"
            autoFocus
          />

          {mode === 'individual' && (
            <>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 mt-4 tracking-wide uppercase">
                Full name <span className="text-gray-300 normal-case">(optional)</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="As it appears on your ID"
                className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-[14px] outline-none focus:border-gray-400 transition"
              />
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-red-50 border border-red-100">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-6 space-y-2">
            <button
              onClick={() =>
                enableEnhanced && mode === 'individual' ? setStep('enhanced') : submitVerification()
              }
              disabled={!idValue.trim()}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition disabled:opacity-40"
              style={{ background: BRAND.green }}
            >
              {enableEnhanced && mode === 'individual' ? 'Continue' : 'Verify'}
            </button>
            {enableEnhanced && mode === 'individual' && (
              <button
                onClick={submitVerification}
                disabled={!idValue.trim()}
                className="w-full py-2.5 text-xs text-gray-500 hover:text-gray-700 transition disabled:opacity-40"
              >
                Skip enhanced verification
              </button>
            )}
          </div>
          <TrustBadge />
        </div>
      )}

      {/* ENHANCED — document + selfie ─────────────────── */}
      {step === 'enhanced' && (
        <div className="p-6">
          <ProgressBar current={3} total={4} />
          <h2 className="text-lg font-bold text-gray-900 mb-1 tracking-tight">
            Enhanced verification
          </h2>
          <p className="text-[13px] text-gray-500 mb-5">
            Optional. Adds a higher trust tier to your account.
          </p>

          {/* Document upload */}
          <div className="mb-3">
            <input
              ref={docInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => handleFile(e.target.files?.[0], setDocFile)}
              className="hidden"
            />
            <button
              onClick={() => docInputRef.current?.click()}
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-gray-200 hover:border-gray-300 transition text-left"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: docFile ? BRAND.green : '#F3F4F6',
                  color: docFile ? '#fff' : '#6B7280',
                }}
              >
                {docFile ? <Check size={16} /> : <FileText size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-[13px]">
                  Upload ID document
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                  {docFile
                    ? docFile.name
                    : 'Passport, Driver’s License, Voter’s Card'}
                </p>
              </div>
              <Upload size={14} className="text-gray-400 flex-shrink-0" />
            </button>
          </div>

          {/* Selfie */}
          <div>
            <input
              ref={selfieInputRef}
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => handleFile(e.target.files?.[0], setSelfieFile)}
              className="hidden"
            />
            <button
              onClick={() => selfieInputRef.current?.click()}
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-gray-200 hover:border-gray-300 transition text-left"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: selfieFile ? BRAND.green : '#F3F4F6',
                  color: selfieFile ? '#fff' : '#6B7280',
                }}
              >
                {selfieFile ? <Check size={16} /> : <Camera size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-[13px]">
                  Take a selfie
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                  {selfieFile ? selfieFile.name : 'Liveness check'}
                </p>
              </div>
              <Camera size={14} className="text-gray-400 flex-shrink-0" />
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-red-50 border border-red-100">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-6 space-y-2">
            <button
              onClick={submitVerification}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition"
              style={{ background: BRAND.green }}
            >
              Submit verification
            </button>
            <button
              onClick={submitVerification}
              className="w-full py-2.5 text-xs text-gray-500 hover:text-gray-700 transition"
            >
              Skip and continue
            </button>
          </div>
          <TrustBadge />
        </div>
      )}

      {/* SUBMITTING ──────────────────────────────────── */}
      {step === 'submitting' && (
        <div className="p-10 text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: BRAND.greenSoft }}
          >
            <Loader2 size={28} className="animate-spin" style={{ color: BRAND.green }} />
          </div>
          <h2 className="text-base font-bold text-gray-900 mb-1.5 tracking-tight">
            Verifying...
          </h2>
          <p className="text-[13px] text-gray-500">This usually takes a few seconds.</p>
          <div className="mt-8 space-y-2 text-left max-w-[260px] mx-auto">
            {[
              'Connecting to identity registry',
              'Cross-checking records',
              'Running watchlist screening',
            ].map((t, i) => (
              <div key={t} className="flex items-center gap-2.5">
                <div
                  className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: BRAND.greenSoft }}
                >
                  <Loader2
                    size={9}
                    className="animate-spin"
                    style={{ color: BRAND.green, animationDelay: `${i * 0.2}s` }}
                  />
                </div>
                <span className="text-[12px] text-gray-600">{t}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RESULT ──────────────────────────────────────── */}
      {step === 'result' && result && (
        <div className="p-6 text-center">
          {/* Status icon */}
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{
              background:
                result.status === 'verified'
                  ? BRAND.greenSoft
                  : result.status === 'review'
                  ? '#FEF3C7'
                  : '#FEE2E2',
            }}
          >
            {result.status === 'verified' && <Check size={28} style={{ color: BRAND.green }} />}
            {result.status === 'review' && (
              <AlertCircle size={28} style={{ color: '#D97706' }} />
            )}
            {result.status === 'failed' && <X size={28} style={{ color: '#DC2626' }} />}
          </div>

          <h2 className="text-[20px] font-bold text-gray-900 mb-1.5 tracking-tight">
            {result.status === 'verified' && 'Identity verified'}
            {result.status === 'review' && 'Manual review needed'}
            {result.status === 'failed' && 'Verification failed'}
          </h2>
          <p className="text-[13px] text-gray-500 mb-6 max-w-xs mx-auto">
            {result.status === 'verified' &&
              `Your ${result.idType} has been confirmed by AfriLink.`}
            {result.status === 'review' &&
              'Your details need a second look. We’ll notify you within 24 hours.'}
            {result.status === 'failed' &&
              'We couldn’t verify the details you provided. Please double-check and try again.'}
          </p>

          {/* Detail panel */}
          {result.status === 'verified' && (result.firstName || result.lastName) && (
            <div className="bg-gray-50 rounded-2xl p-4 mb-5 text-left">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Verified details
              </p>
              <div className="space-y-1.5">
                {(result.firstName || result.lastName) && (
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-gray-500">Name</span>
                    <span className="text-[13px] font-medium text-gray-900">
                      {[result.firstName, result.middleName, result.lastName]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  </div>
                )}
                {result.dateOfBirth && (
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-gray-500">
                      {mode === 'business' ? 'Registered' : 'Date of birth'}
                    </span>
                    <span className="text-[13px] font-medium text-gray-900">
                      {result.dateOfBirth}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-[12px] text-gray-500">Tier</span>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                    style={{ background: BRAND.greenSoft, color: BRAND.greenDark }}
                  >
                    {result.tier}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            {result.status === 'verified' && onClose && (
              <button
                onClick={onClose}
                className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition"
                style={{ background: BRAND.green }}
              >
                Done
              </button>
            )}
            {result.status === 'review' && onClose && (
              <button
                onClick={onClose}
                className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition"
                style={{ background: BRAND.green }}
              >
                Got it
              </button>
            )}
            {result.status === 'failed' && (
              <button
                onClick={startOver}
                className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition"
                style={{ background: BRAND.green }}
              >
                Try again
              </button>
            )}
            <button
              onClick={startOver}
              className="w-full py-2.5 text-xs text-gray-500 hover:text-gray-700 transition"
            >
              Verify another
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-100">
            <Logo size={16} />
            <span className="text-[11px] text-gray-400 font-medium">
              Verified by AfriLink
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyWidget;
